// Resolución de URLs firmadas de R2 vía Cloudflare Worker (vortex-media-signer).
// Solo los estudiantes autenticados (ID token de Firebase) reciben URLs de corta
// duración; el bucket en privado impide descargas directas.
import { auth } from '../firebase';

const WORKER_URL = (import.meta.env.VITE_MEDIA_SIGNER_URL || '').replace(/\/$/, '');
const R2_PUBLIC_BASE = (import.meta.env.VITE_R2_PUBLIC_URL || '').replace(/\/$/, '');
const CACHE = new Map(); // urlPublica -> { signed, exp }
const TTL_MS = 8 * 60 * 1000; // re-firmar antes de que expire la URL (~15 min)
const PROMISES = new Map(); // urlPublica -> Promise (dedupe llamadas simultáneas)

async function getIdToken() {
  const u = auth?.currentUser;
  if (!u) return null;
  try {
    return await u.getIdToken();
  } catch {
    return null;
  }
}

export function isR2Url(url) {
  return typeof url === 'string' && R2_PUBLIC_BASE && url.startsWith(R2_PUBLIC_BASE);
}

async function requestSigned(urls) {
  if (!WORKER_URL) return {};
  const token = await getIdToken();
  if (!token) return {};
  try {
    const res = await fetch(`${WORKER_URL}/api/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ urls }),
    });
    if (!res.ok) return {};
    const data = await res.json();
    return data.signed || {};
  } catch (e) {
    console.warn('[mediaSigner] No se pudieron firmar URLs:', e);
    return {};
  }
}

// Devuelve la URL firmada (o la original si no es R2 / no se pudo firmar).
export function signUrl(url) {
  if (!isR2Url(url)) return Promise.resolve(url);

  const hit = CACHE.get(url);
  if (hit && hit.exp > Date.now()) return Promise.resolve(hit.signed);

  if (PROMISES.has(url)) return PROMISES.get(url);

  const p = (async () => {
    const map = await requestSigned([url]);
    const signed = map[url] || url;
    CACHE.set(url, { signed, exp: Date.now() + TTL_MS });
    PROMISES.delete(url);
    return signed;
  })();
  PROMISES.set(url, p);
  return p;
}

// Firma varias URLs en una sola llamada al Worker (más eficiente en grids).
export async function signUrls(urls) {
  const unique = [...new Set((urls || []).filter((u) => isR2Url(u)))];
  const toFetch = unique.filter((u) => {
    const hit = CACHE.get(u);
    return !hit || hit.exp <= Date.now();
  });
  let map = {};
  if (toFetch.length > 0) {
    map = await requestSigned(toFetch);
    const now = Date.now();
    toFetch.forEach((u) => {
      CACHE.set(u, { signed: map[u] || u, exp: now + TTL_MS });
    });
  }
  return (urls || []).map((u) => {
    if (!isR2Url(u)) return u;
    const hit = CACHE.get(u);
    return (hit && hit.signed) || u;
  });
}

// URL del proxy de PDF (para pdf.js) o la original si no es R2.
export function pdfProxyUrl(url) {
  if (!isR2Url(url) || !WORKER_URL) return url;
  return `${WORKER_URL}/api/pdf?url=${encodeURIComponent(url)}`;
}

// Construye la URL pública de un objeto del bucket a partir de su key.
export function r2PublicUrl(key) {
  const k = String(key || '').replace(/^\//, '');
  if (!R2_PUBLIC_BASE || !k) return '';
  return `${R2_PUBLIC_BASE}/${k.split('/').map(encodeURIComponent).join('/')}`;
}

// Lista los objetos del bucket R2 vía el Worker (requiere sesión de admin).
// Devuelve { objects: [{key, size}], folders: [prefijos/"carpetas"], truncated, next } —
// usa `cursor` (next) para paginar hasta 1000 objetos por página. Pasa `delimiter='/'
// para agrupar en carpetas (los prefijos llegan en `folders`).
export async function listR2Objects({ prefix = '', delimiter = '', cursor = '', max = 1000 } = {}) {
  if (!WORKER_URL) throw new Error('Worker de media no configurado (VITE_MEDIA_SIGNER_URL).');
  const token = await getIdToken();
  if (!token) throw new Error('No autenticado para listar el bucket.');
  const params = new URLSearchParams();
  if (prefix) params.set('prefix', prefix);
  if (delimiter) params.set('delimiter', delimiter);
  if (cursor) params.set('cursor', cursor);
  if (max && max !== 1000) params.set('max', String(max));
  const res = await fetch(`${WORKER_URL}/api/list?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `HTTP ${res.status}`);
  }
  return res.json();
}

const MULTIPART_PART_SIZE = 8 * 1024 * 1024;
const MULTIPART_MAX_ATTEMPTS = 3;

async function workerRequest(path, options = {}) {
  if (!WORKER_URL) throw new Error('Worker URL no configurada');
  const token = await getIdToken();
  if (!token) throw new Error('No autenticado');
  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(`${WORKER_URL}${path}`, { ...options, headers });
  if (!res.ok) {
    let detail = '';
    try { const data = await res.json(); detail = data?.error || data?.detail || ''; } catch {}
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return res.status === 204 ? null : res.json();
}

export async function startR2MultipartUpload({ filename, contentType, size }) {
  const data = await workerRequest('/api/r2-multipart/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, contentType, size }),
  });
  return data;
}

export async function uploadR2MultipartPart({ key, uploadId, partNumber, body, contentType }) {
  const params = new URLSearchParams({ key, uploadId, partNumber: String(partNumber) });
  return workerRequest(`/api/r2-multipart/part?${params.toString()}`, {
    method: 'PUT',
    headers: { 'Content-Type': contentType || 'application/octet-stream' },
    body,
  });
}

export async function completeR2MultipartUpload({ key, uploadId, parts }) {
  return workerRequest('/api/r2-multipart/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, uploadId, parts }),
  });
}

export async function abortR2MultipartUpload({ key, uploadId }) {
  const params = new URLSearchParams({ key, uploadId });
  await workerRequest(`/api/r2-multipart/abort?${params.toString()}`, { method: 'DELETE' });
}

export async function uploadFileToR2(file, { onProgress, partSize = MULTIPART_PART_SIZE } = {}) {
  if (!file || !file.size) throw new Error('Archivo inválido');
  if (!WORKER_URL) throw new Error('Worker URL no configurada');

  const start = await startR2MultipartUpload({
    filename: file.name,
    contentType: file.type || 'application/zip',
    size: file.size,
  });

  const resolvedPartSize = Math.max(5 * 1024 * 1024, Number(start.partSize) || partSize);
  const totalParts = Math.ceil(file.size / resolvedPartSize);
  const parts = [];
  let uploadedBytes = 0;

  try {
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const startOffset = (partNumber - 1) * resolvedPartSize;
      const end = Math.min(file.size, startOffset + resolvedPartSize);
      const chunk = file.slice(startOffset, end);

      let attempt = 0;
      let lastError;
      while (attempt < MULTIPART_MAX_ATTEMPTS) {
        try {
          const uploadedPart = await uploadR2MultipartPart({
            key: start.key,
            uploadId: start.uploadId,
            partNumber,
            body: chunk,
            contentType: file.type || 'application/octet-stream',
          });
          parts.push({ partNumber: uploadedPart.partNumber || partNumber, etag: uploadedPart.etag });
          uploadedBytes += chunk.size;
          if (onProgress) onProgress({ loaded: uploadedBytes, total: file.size, percent: (uploadedBytes / file.size) * 100, partNumber, totalParts });
          break;
        } catch (err) {
          attempt++;
          lastError = err;
          if (attempt >= MULTIPART_MAX_ATTEMPTS) break;
          await new Promise((r) => setTimeout(r, 400 * attempt));
        }
      }
      if (attempt >= MULTIPART_MAX_ATTEMPTS) {
        throw lastError || new Error(`Error en la parte ${partNumber}`);
      }
    }

    const completed = await completeR2MultipartUpload({ key: start.key, uploadId: start.uploadId, parts });
    if (onProgress) onProgress({ loaded: file.size, total: file.size, percent: 100, partNumber: totalParts, totalParts });
    return { key: completed.key, publicUrl: completed.publicUrl, etag: completed.etag, size: file.size, parts };
  } catch (err) {
    try { await abortR2MultipartUpload({ key: start.key, uploadId: start.uploadId }); } catch {}
    throw err;
  }
}

export { WORKER_URL, R2_PUBLIC_BASE };
